import React from 'react';

import type { EventRecord } from '@vlabs/api/artemy/model';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { MultiSample } from '@vlabs/monokit/shared/ui/multi-sample';
import { StreamLinkPoint3 } from '@vlabs/monokit/shared/ui/stream-link/StreamLink.point3';

import st from './EventHistoryCard.module.sass';

interface EventHistoryCardPoint3Props {
  event: EventRecord;
  isActive: boolean;
  onClick: (event: EventRecord) => void;
}

function EventHistoryCardPoint3({
  event,
  isActive,
  onClick,
}: EventHistoryCardPoint3Props): React.JSX.Element {
  const { t } = useTranslation();
  const location = buildLocationString(event);
  const createdAt = formatDateTime(event?.create_time);

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetectionUrl = getArtemySampleUrl('bodies', bodyDetection?.sample_id);

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceDetectionUrl = getArtemySampleUrl('faces', faceDetection?.sample_id);

  return (
    <div
      className={cn(st.Root, {
        [st.Root_active]: isActive,
      })}
    >
      <button
        aria-label="Event history"
        className={st.SelectCardButton}
        data-testid="workspace.eventHistoryCard.selectCardButton"
        onClick={() => onClick(event)}
        type="button"
      />

      <div className={st.Sample}>
        <MultiSample
          className={st.MultiSample}
          data-testid="workspace.eventHistoryCard.sample"
          primary={{
            imageUrl: faceDetectionUrl,
            type: 'face',
          }}
          secondary={{
            imageUrl: bodyDetectionUrl,
            type: 'body',
          }}
        />
      </div>

      <div className={st.EventInfo}>
        <div className={st.Main}>
          <div className={st.TextBlock}>
            <span data-testid="workspace.eventHistoryCard.createTime">
              {createdAt}
            </span>

            <div
              className={st.StreamLinkText}
              data-testid="workspace.eventHistoryCard.sources"
            >
              <StreamLinkPoint3
                className={st.StreamLink}
                hasTruncatedLink
                hideId
                kind="secondary"
                placeholders={{
                  removed: t('lunapoint:карточка события.videostream.removed'),
                  unavailable: t('lunapoint:карточка события.videostream.unavailable'),
                }}
                size="medium"
                streamId={event.stream_id}
              />
            </div>
          </div>

          <div className={st.Tags}>
            <SimilarityTag
              data-testid="workspace.eventHistoryCard.similarityTagType"
              icon={<DetectionIcon />}
              size="medium"
              title={t('lunapoint:карточка персоны.карточка')}
              value={event?.top_match?.similarity ?? 0}
            />
          </div>
        </div>

        <div
          className={cn(st.Footer, {
            [st.EmptyLocationText]: location === t('lunapoint:адрес отсутствует'),
          })}
          data-testid="workspace.eventHistoryCard.location"
        >
          <TruncatedText
            hasTruncation
            tooltipContent={location}
          >
            {location}
          </TruncatedText>
        </div>
      </div>
    </div>
  );
}

export { EventHistoryCardPoint3 };
